/**
 * @fileoverview The device camera, as a stream you can start, stop and take one frame from.
 *
 * Pulled out of the photo dialog, which was holding a MediaStream, a video
 * element, a canvas and a portrait-rotation quirk alongside a date picker and
 * a notes field. None of that is about photographing a plant in particular,
 * and a leaked camera stream is the kind of bug that only shows up as a light
 * that will not turn off.
 *
 * Where: EditPhotoDialog, UploadPhotoDialog and TrayUploader, through
 * PhotoSourcePicker.
 * API: none (the browser's getUserMedia).
 * Settings: none.
 * Words (en/es): plants.photo.source.* (cameraDenied, cameraMissing,
 * cameraBusy, cameraUnavailable).
 *
 * @module useCamera
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { t } from '@/shared/i18n/i18nSetup';
import { debugLog, APP_CONSTANTS } from '@/config/config';
import { CAMERA_CAPTURE_FILE_NAME, CAMERA_FACING_MODE, CAMERA_PAINT_WAIT_MS } from '@/config/tunables/plantList';

/** What a caller gets back: a video element to render, three verbs, and why it would not start. */
export interface Camera {
  /** Attach to a <video> element for the live preview. */
  videoRef: React.RefObject<HTMLVideoElement>;
  /** True once the preview should be on screen. */
  isActive: boolean;
  /**
   * Why the camera would not start, in the reader's language, or null. Set
   * when start fails; cleared by the next start or stop.
   */
  error: string | null;
  /** Asks for permission and starts the stream. Failure turns isActive off and sets error. */
  start: () => Promise<void>;
  /** Stops every track and releases the camera. Safe to call when idle. */
  stop: () => void;
  /**
   * Grabs the current frame as a JPEG.
   *
   * The camera is stopped either way — a captured frame means the gardener is
   * done pointing it at things.
   *
   * @returns The frame as a File, or null when there was nothing to capture.
   */
  capture: () => Promise<File | null>;
}

/**
 * Why the camera would not start, said so a gardener can do something about it.
 *
 * getUserMedia fails by name: NotAllowedError when permission was refused
 * (now or for good), NotFoundError when there is no camera, NotReadableError
 * when another app has it. Everything else, including a browser with no
 * camera API at all, is "could not be started".
 *
 * @param err - Whatever starting the camera threw.
 * @returns A translated sentence.
 */
function cameraFailure(err: unknown): string {
  const name = err instanceof Error ? err.name : '';
  if (name === 'NotAllowedError' || name === 'SecurityError' || name === 'PermissionDeniedError') {
    return t('plants.photo.source.cameraDenied');
  }
  if (name === 'NotFoundError' || name === 'OverconstrainedError' || name === 'DevicesNotFoundError') {
    return t('plants.photo.source.cameraMissing');
  }
  if (name === 'NotReadableError' || name === 'TrackStartError') {
    return t('plants.photo.source.cameraBusy');
  }
  return t('plants.photo.source.cameraUnavailable');
}

/**
 * Borrows the device camera for as long as this component is mounted.
 *
 * The stream is stopped on unmount whatever else happens, which is the whole
 * reason this is a hook rather than three loose functions.
 *
 * @returns The video ref, whether it is on, why it failed, and start, stop and capture.
 */
export function useCamera(): Camera {
  /** The <video> the preview plays in. */
  const videoRef = useRef<HTMLVideoElement>(null);
  /** The live stream, held so stop can end every track. */
  const streamRef = useRef<MediaStream | null>(null);
  /**
   * Which start() is the current one. Bumped by every start and stop, so a
   * start still waiting on permission when the gardener cancels, or the
   * dialog closes, knows to hand its stream straight back rather than leave
   * the camera light on with nothing showing it.
   */
  const attemptRef = useRef(0);
  const [isActive, setIsActive] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /** Releases the camera, drops the preview, and forgets why it last failed. */
  const stop = useCallback(() => {
    attemptRef.current += 1;
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setIsActive(false);
    setError(null);
  }, []);

  /** Turns the preview on, then asks for the stream to fill it. */
  const start = useCallback(async () => {
    debugLog('useCamera', 'Starting camera...');
    const attempt = ++attemptRef.current;
    setError(null);
    setIsActive(true);
    let stream: MediaStream | null = null;

    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('Camera API not supported in this browser');
      }

      // The <video> is rendered by isActive, which has not painted yet.
      await new Promise((resolve) => setTimeout(resolve, CAMERA_PAINT_WAIT_MS));

      stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: CAMERA_FACING_MODE,
          width: { ideal: APP_CONSTANTS.PHOTO.MAX_WIDTH },
          height: { ideal: APP_CONSTANTS.PHOTO.MAX_HEIGHT },
        },
      });

      // Stopped while permission was being asked: give the camera back.
      if (attempt !== attemptRef.current) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        streamRef.current = stream;
        await videoRef.current.play();
      } else {
        throw new Error('Video element not found');
      }
    } catch (err) {
      debugLog('useCamera', 'Detailed camera error:', err);
      // A stream that was granted but could not be shown is still holding
      // the camera.
      if (stream && streamRef.current !== stream) stream.getTracks().forEach((track) => track.stop());
      // Said, not swallowed: a tap on "Take a photo" that put the rows back
      // with no word was the whole report of a refused permission.
      if (attempt === attemptRef.current) {
        stop();
        setError(cameraFailure(err));
      }
    }
  }, [stop]);

  /** Draws the current frame to a canvas and hands it back as a JPEG file. */
  const capture = useCallback(async (): Promise<File | null> => {
    const video = videoRef.current;
    if (!video) return null;

    /*
     * The frame exactly as the preview showed it, and no turning.
     *
     * There used to be a quarter turn here whenever the browser window was
     * taller than it was wide, and it did two things wrong at once.
     *
     * It turned the frame but left the canvas the shape it started — so a
     * 1920x1080 frame was rotated into a 1080-wide, 1920-tall space on a
     * canvas still 1920x1080. Most of the picture fell off the bottom and the
     * part of the canvas nothing was ever drawn on stayed empty, which a JPEG
     * writes as black. A photograph taken upright came out lying down with a
     * black band across it, missing whatever had been at the bottom of it.
     *
     * And the test was the wrong question. Whether the browser window is
     * portrait says nothing about how the camera is delivering its frames;
     * getUserMedia on a phone already hands back the picture the right way up,
     * which is why the live preview looks right. Turning it again is what put
     * it on its side.
     *
     * The preview is a plain video element with no transform on it, so
     * drawing the frame untouched makes the photograph and the preview the
     * same thing by construction — which is the only version of this a
     * gardener can check before pressing the button.
     */
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    ctx.drawImage(video, 0, 0);

    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob(resolve, 'image/jpeg', APP_CONSTANTS.PHOTO.QUALITY);
    });

    stop();
    return blob ? new File([blob], CAMERA_CAPTURE_FILE_NAME, { type: 'image/jpeg' }) : null;
  }, [stop]);

  // Whatever happens to the component, the light goes off.
  useEffect(() => stop, [stop]);

  return { videoRef, isActive, error, start, stop, capture };
}
